import React, { useState, useMemo } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { ListChecks, Loader2, Users, AlertTriangle } from 'lucide-react';
import { Field, fmtDate } from './design';

// Each session a window will report, with what it carries for its DEX service type: the Stage 3
// fields (engagement time, trip time and distance, location, overnight, meals) come from the service's
// defaults and the visit, and can be set for one session here. A meal that did not go out is marked
// not delivered and left out. Filter by service for a meal run.

const NUM = [
  ['ENGAGEMENTTIME', 'engagementMinutes', 'Engagement (min)'], ['TRIPTIME', 'tripMinutes', 'Trip time (min)'], ['TRIPDISTANCE', 'tripKm', 'Trip (km)'],
  ['ASSISTANCEQUANTITY', 'quantityPerSession', 'Quantity'], ['MAINMEALCOUNT', 'mainMeals', 'Main meals'], ['LIGHTMEALCOUNT', 'lightMeals', 'Light meals'],
  ['DESSERTCOUNT', 'desserts', 'Desserts'], ['BEVERAGECOUNT', 'beverages', 'Beverages'], ['SNACKCOUNT', 'snacks', 'Snacks'],
];
const BOOL = [['OVERNIGHTSUPPORTINCLUDED', 'overnight', 'Overnight support'], ['MEALPROVIDED', 'mealProvided', 'Meal provided'],
  ['MEALCOOKEDOFFSITE', 'mealCookedOffsite', 'Cooked offsite'], ['ITEMSTEXTUREMODIFIED', 'textureModified', 'Texture modified']];
// The value shown for a field: quantity is stored per session as `quantity`.
const shown = (v, key) => (key === 'quantityPerSession' ? v.quantity : v[key]);
// A session location in words, from the reference data; the code itself is never shown.
const locationName = (lists, code) => {
  const hit = (lists?.ServiceLocationSetting || []).find((x) => x.code === code);
  return hit?.desc || 'Location set';
};

function SessionEdit({ s, lists, onDone }) {
  const req = new Set(s.requirements);
  const [f, setF] = useState({ ...s.set });
  const [saving, setSaving] = useState(false);
  const save = async (clear) => {
    setSaving(true);
    try {
      await api.put('/aged-care/dex/sessions/values', { shiftId: s.shiftId, clientId: s.clientUuid, fields: clear ? {} : f });
      toast.success(clear ? 'Back to the service defaults' : 'Saved for this session'); onDone();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not save'); } finally { setSaving(false); }
  };
  return (
    <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50/60 p-3 space-y-2">
      <div className="flex flex-wrap items-end gap-3">
        {NUM.filter(([code]) => req.has(code)).map(([code, key, label]) => (
          <Field key={key} label={label}>
            {(id) => <input id={id} type="number" min="0" className="input-field w-24" placeholder={String(shown(s.values, key) ?? '')} value={f[key] ?? ''} onChange={(e) => setF((p) => ({ ...p, [key]: e.target.value }))} />}
          </Field>
        ))}
        {req.has('SERVICELOCATIONSETTINGCODE') && (
          <Field label="Location">
            {(id) => (
              <select id={id} className="input-field" value={f.locationCode || ''} onChange={(e) => setF((p) => ({ ...p, locationCode: e.target.value }))}>
                <option value="">Service default</option>
                {(lists.ServiceLocationSetting || []).map((x) => <option key={x.code} value={x.code}>{x.desc || 'Not named'}</option>)}
              </select>
            )}
          </Field>
        )}
        {BOOL.filter(([code]) => req.has(code)).map(([code, key, label]) => (
          <Field key={key} label={label}>
            {(id) => (
              <select id={id} className="input-field" value={f[key] === true ? 'yes' : f[key] === false ? 'no' : ''} onChange={(e) => setF((p) => ({ ...p, [key]: e.target.value === '' ? undefined : e.target.value === 'yes' }))}>
                <option value="">Default ({s.values[key] ? 'yes' : 'no'})</option><option value="yes">Yes</option><option value="no">No</option>
              </select>
            )}
          </Field>
        ))}
      </div>
      <label className="flex items-center gap-2 text-[12.5px] text-slate-700">
        <input type="checkbox" checked={!!f.notDelivered} onChange={(e) => setF((p) => ({ ...p, notDelivered: e.target.checked }))} /> Not delivered: leave it out of DEX
      </label>
      {f.notDelivered && (
        <Field label="Why">{(id) => <input id={id} className="input-field" value={f.notDeliveredReason || ''} onChange={(e) => setF((p) => ({ ...p, notDeliveredReason: e.target.value }))} placeholder="For example, nobody home" />}</Field>
      )}
      <div className="flex justify-end gap-2">
        {Object.keys(s.set || {}).length > 0 && <button type="button" className="btn-ghost btn-sm" disabled={saving} onClick={() => save(true)}>Use the defaults</button>}
        <button type="button" className="btn-ghost btn-sm" onClick={onDone}>Cancel</button>
        <button type="button" className="btn-primary btn-sm" disabled={saving} onClick={() => save(false)}>Save</button>
      </div>
    </div>
  );
}

export default function DexSessions({ range, lists }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(null);
  const [service, setService] = useState('');
  const load = async () => {
    setBusy(true); setError(null);
    try { const r = await api.get('/aged-care/dex/sessions', { params: { from: range[0], to: range[1] } }); setData(r.data); }
    catch (e) { const msg = e.response?.data?.message || 'Could not load the sessions'; setError(msg); toast.error(msg); }
    finally { setBusy(false); }
  };
  const services = useMemo(() => [...new Set((data?.sessions || []).map((s) => s.serviceName))].sort(), [data]);
  const rows = (data?.sessions || []).filter((s) => !service || s.serviceName === service);
  const n = data?.notes || {};
  const summary = (s) => {
    const req = new Set(s.requirements); const v = s.values; const out = [];
    if (v.minutes != null) out.push(`${v.minutes} min`);
    for (const [code, key, label] of NUM) if (req.has(code) && shown(v, key) != null) out.push(`${label.replace(/ \(.*\)/, '')} ${shown(v, key)}${key === 'tripKm' ? ' km' : key.endsWith('Minutes') ? ' min' : ''}`);
    if (v.locationCode) out.push(locationName(lists, v.locationCode).toLowerCase());
    if (v.mealProvided === false) out.push('no meal');
    if (v.fees != null) out.push(`fees $${Number(v.fees).toFixed(2)}`);
    return out.join(' · ');
  };
  return (
    <div className="mt-4 rounded-lg border border-slate-200 p-3 text-[13px]">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-medium text-slate-800 flex items-center gap-1.5"><ListChecks size={14} /> Sessions in this window</p>
        <div className="flex items-center gap-2">
          {services.length > 1 && (
            <select aria-label="Service" className="input-field !w-auto !py-1" value={service} onChange={(e) => setService(e.target.value)}>
              <option value="">All services</option>{services.map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          )}
          <button type="button" className="btn-secondary btn-sm" onClick={load} disabled={busy || !range[0] || !range[1]}>
            {busy ? <Loader2 size={13} className="animate-spin" /> : <ListChecks size={13} />} {data ? 'Refresh' : 'Check sessions'}
          </button>
        </div>
      </div>
      {/* Before the first check, say what it does: an empty box read as "no sessions". */}
      {!data && !busy && !error && (
        <p className="mt-1 text-[12px] text-slate-500">
          Check sessions lists every visit this window will report to DEX, with what each carries for its service (time, trips, meals, location).
          Correct one session here, or mark a meal that did not go out as not delivered so it is left out.
        </p>
      )}
      {!data && error && (
        <p role="alert" className="mt-1 text-[12px] text-red-800">{error} <button type="button" className="underline ml-1" onClick={load}>Try again</button></p>
      )}
      {data && (
        <>
          <p className="mt-1 text-[12px] text-slate-500">
            {data.sessions.length} session{data.sessions.length === 1 ? '' : 's'} from {fmtDate(data.from)} to {fmtDate(data.to)}.
            {' '}Values come from the service's defaults and the visit (kilometres from the worker's timesheet); set one here and it wins.
          </p>
          {(n.notFunded || []).length > 0 && <p className="text-slate-600">Not reported, not CHSP funded: {n.notFunded.map((x) => `${x.name} (${x.sessions})`).join('; ')}</p>}
          {n.notDelivered > 0 && <p className="text-slate-600">Marked not delivered: {n.notDelivered}</p>}
          {/* These are requirements Rostery has no name for (DEX added them since), so the line names
              the services they are on and how many, not DEX's code for them. */}
          {(n.unknownRequirements || []).length > 0 && (
            <p className="text-amber-800">
              <AlertTriangle size={13} className="inline -mt-0.5" /> DEX asks for {n.unknownRequirements.length === 1 ? 'a detail' : `${n.unknownRequirements.length} details`} Rostery does not fill yet,
              {' '}on {[...new Set(n.unknownRequirements.flatMap((x) => x.serviceTypes || []))].join(', ') || 'one of your services'}. Tell Rostery support.
            </p>
          )}
          <ul className="mt-2 divide-y divide-slate-100">
            {rows.map((s) => {
              const key = `${s.shiftId}|${s.clientUuid || ''}`;
              return (
                <li key={key} className="py-2">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <span className="min-w-0">
                      <span className="tabular-nums text-slate-500 mr-2">{fmtDate(s.date)}</span>
                      <span className="font-medium text-slate-800">{s.group ? <><Users size={13} className="inline -mt-0.5" /> Group of {s.names.length}</> : s.names[0]}</span>
                      <span className="text-slate-500"> · {s.serviceName}</span>
                      {Object.keys(s.set || {}).length > 0 && <span className="ml-2 badge badge-blue">set for this session</span>}
                      <span className="block text-[12px] text-slate-600">{summary(s)}{s.group ? ` · ${s.names.join(', ')}` : ''}</span>
                      {s.warnings.length > 0 && <span className="block text-[12px] text-amber-700">{s.warnings.join('; ')}</span>}
                    </span>
                    {open !== key && <button type="button" className="btn-ghost btn-sm" onClick={() => setOpen(key)}>Edit</button>}
                  </div>
                  {open === key && <SessionEdit s={s} lists={lists} onDone={() => { setOpen(null); load(); }} />}
                </li>
              );
            })}
            {rows.length === 0 && <li className="py-2 text-slate-500">No sessions to report in this window.</li>}
          </ul>
        </>
      )}
    </div>
  );
}
